import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { API, createTranslator } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function CareersPage({ language }) {
  const t = createTranslator(language);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const submissionType = params.get("type") === "share" ? "share" : "apply";
  const submissionLabel =
    submissionType === "share"
      ? t("careers.ctaSecondary", "Share Resume & Credentials")
      : t("careers.ctaPrimary", "Apply With Your Profile");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      role: "",
      experience: "",
      location: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setSubmitStatus("loading");
    try {
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("email", data.email);
      formData.append("phone", data.phone);
      formData.append("role", data.role);
      formData.append("experience", data.experience);
      formData.append("location", data.location);
      formData.append("message", data.message);
      formData.append("source", submissionType);
      const resumeFile = data.resume?.[0];
      if (!resumeFile) {
        throw new Error(t("form.resumeRequired", "Resume PDF is required"));
      }
      formData.append("resume", resumeFile);

      await axios.post(`${API}/careers`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitStatus("success");
      reset();
    } catch (error) {
      console.error(error);
      setSubmitStatus("error");
    }
  };

  return (
    <div className="App" data-testid="careers-page">
      <Header language={language} />
      <main className="pt-24" data-testid="careers-main-content">
        <section className="py-16 bg-[#003366] text-white" data-testid="career-form-hero">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <p
              className="text-sm uppercase tracking-[0.4em] text-orange-200"
              data-testid="career-form-eyebrow"
            >
              {t("careers.eyebrow", "Careers")}
            </p>
            <h1
              className="font-heading text-4xl md:text-5xl uppercase mt-4"
              data-testid="career-form-title"
            >
              {submissionLabel}
            </h1>
            <p className="mt-4 text-slate-100" data-testid="career-form-subtitle">
              {t(
                "careersPage.subtitle",
                "Submit your profile to join our EPC, material supply, and O&M delivery teams."
              )}
            </p>
          </div>
        </section>
        <section className="py-16 bg-[#F5F7FA]" data-testid="career-form-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white border border-slate-200 p-6" data-testid="career-form-card">
              <div>
                <h2
                  className="font-heading text-3xl text-[#003366]"
                  data-testid="career-form-heading"
                >
                  {t("careersPage.candidateDetails", "Candidate Details")}
                </h2>
                <p className="mt-3 text-slate-600" data-testid="career-form-description">
                  {t(
                    "careersPage.candidateDescription",
                    "Provide accurate contact details and role preferences. Submissions are stored in our database for review by the recruitment team."
                  )}
                </p>
              </div>
              <form
                className="mt-6 space-y-4"
                onSubmit={handleSubmit(onSubmit)}
                data-testid="career-form"
              >
                <div>
                  <label className="text-sm font-semibold" data-testid="career-name-label">
                    {t("careersPage.nameLabel", "Full Name")}
                  </label>
                  <Input
                    className="mt-2 rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t("careersPage.namePlaceholder", "Enter your full name")}
                    {...register("name", { required: t("form.nameRequired", "Name is required") })}
                    data-testid="career-name-input"
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500 mt-1" data-testid="career-name-error">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="career-email-label">
                    {t("careersPage.emailLabel", "Email Address")}
                  </label>
                  <Input
                    className="mt-2 rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t("careersPage.emailPlaceholder", "Enter your email")}
                    {...register("email", {
                      required: t("form.emailRequired", "Email is required"),
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: t("form.emailInvalid", "Enter a valid email"),
                      },
                    })}
                    data-testid="career-email-input"
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500 mt-1" data-testid="career-email-error">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="career-phone-label">
                    {t("careersPage.phoneLabel", "Phone Number")}
                  </label>
                  <Input
                    className="mt-2 rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t("careersPage.phonePlaceholder", "Enter your phone number")}
                    {...register("phone", {
                      required: t("form.phoneRequired", "Phone number is required"),
                    })}
                    data-testid="career-phone-input"
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-500 mt-1" data-testid="career-phone-error">
                      {errors.phone.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="career-role-label">
                    {t("careersPage.roleLabel", "Role / Position")}
                  </label>
                  <Input
                    className="mt-2 rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t("careersPage.rolePlaceholder", "Role you are applying for")}
                    {...register("role", { required: t("form.roleRequired", "Role is required") })}
                    data-testid="career-role-input"
                  />
                  {errors.role && (
                    <p className="text-sm text-red-500 mt-1" data-testid="career-role-error">
                      {errors.role.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="career-experience-label">
                    {t("careersPage.experienceLabel", "Years of Experience")}
                  </label>
                  <Input
                    className="mt-2 rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t("careersPage.experiencePlaceholder", "Total experience")}
                    {...register("experience", {
                      required: t("form.experienceRequired", "Experience is required"),
                    })}
                    data-testid="career-experience-input"
                  />
                  {errors.experience && (
                    <p className="text-sm text-red-500 mt-1" data-testid="career-experience-error">
                      {errors.experience.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="career-location-label">
                    {t("careersPage.locationLabel", "Current Location")}
                  </label>
                  <Input
                    className="mt-2 rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t("careersPage.locationPlaceholder", "City / State")}
                    {...register("location", {
                      required: t("form.locationRequired", "Location is required"),
                    })}
                    data-testid="career-location-input"
                  />
                  {errors.location && (
                    <p className="text-sm text-red-500 mt-1" data-testid="career-location-error">
                      {errors.location.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="career-resume-label">
                    {t("careersPage.resumeLabel", "Upload Resume (PDF)")}
                  </label>
                  <input
                    type="file"
                    accept="application/pdf"
                    className="mt-2 w-full rounded-none border border-gray-300 bg-gray-50 px-3 py-2 text-sm"
                    {...register("resume", {
                      required: t("form.resumeRequired", "Resume PDF is required"),
                      validate: (files) =>
                        files?.[0]?.type === "application/pdf" ||
                        t("form.resumeInvalid", "Only PDF files are allowed"),
                    })}
                    data-testid="career-resume-input"
                  />
                  {errors.resume && (
                    <p className="text-sm text-red-500 mt-1" data-testid="career-resume-error">
                      {errors.resume.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="career-message-label">
                    {t("careersPage.messageLabel", "Message")}
                  </label>
                  <Textarea
                    className="mt-2 min-h-[120px] rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t(
                      "careersPage.messagePlaceholder",
                      "Tell us about your experience"
                    )}
                    {...register("message", {
                      required: t("form.messageRequired", "Message is required"),
                    })}
                    data-testid="career-message-input"
                  />
                  {errors.message && (
                    <p className="text-sm text-red-500 mt-1" data-testid="career-message-error">
                      {errors.message.message}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#003366] text-white hover:bg-[#002244] rounded-none px-8 py-4 font-bold uppercase tracking-wider"
                  disabled={isSubmitting}
                  data-testid="career-submit-button"
                >
                  {isSubmitting
                    ? t("careersPage.submitting", "Submitting...")
                    : t("careersPage.submit", "Submit Profile")}
                </Button>
                {submitStatus === "success" && (
                  <p className="text-green-600 text-sm" data-testid="career-success-message">
                    {t(
                      "careersPage.success",
                      "Thank you. Your profile has been received by our team."
                    )}
                  </p>
                )}
                {submitStatus === "error" && (
                  <p className="text-red-600 text-sm" data-testid="career-error-message">
                    {t("careersPage.error", "Submission failed. Please try again.")}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer language={language} />
    </div>
  );
}
